import axios from "axios";

const BASE_URL = "https://krishimart-back.onrender.com/api/v2";

// Create axios instance with default configs
const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to attach tokens from cookies if available
apiClient.interceptors.request.use(
  config => {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('access-token='))
      ?.split('=')[1];
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  error => Promise.reject(error)
);

export const createUser = async (userData) => {
  try {
    const formData = new FormData();

    // Add the basic user fields
    formData.append('name', userData.name);
    formData.append('email', userData.email);
    formData.append('password', userData.password);
    formData.append('phoneNumber', userData.phoneNumber);

    // Add addresses as JSON string
    formData.append('addresses', JSON.stringify(userData.addresses));

    // Handle avatar (file) field
    if (userData.avatar instanceof File) {
      formData.append('file', userData.avatar);
    } else {
      // Explicitly append an empty file field to satisfy backend (optional)
      formData.append('file', ''); // Or omit this if backend can handle missing file
      console.log("No avatar file selected");
    }

    console.log("Sending to server:");
    for (let pair of formData.entries()) {
      console.log(`${pair[0]}: ${pair[1] instanceof File ? `File: ${pair[1].name}` : pair[1]}`);
    }

    const res = await axios.post(`${BASE_URL}/user/create-user`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return res.data;
  } catch (err) {
    console.error("User creation error:", err);
    if (err.response) {
      console.error("Server error details:", err.response.data);
      throw err.response?.data?.message || err.response?.data?.msg || `Server error: ${err.response.status}`;
    } else {
      throw err.message || "User creation failed";
    }
  }
};
// Login a user
export const loginUser = async (loginData) => {
  try {
    const res = await apiClient.post('/user/login-user', loginData);
    return res.data;
  } catch (err) {
    console.error("Login error:", err);
    throw err.response?.data?.message || err.response?.data?.msg || "Invalid credentials";
  }
};

// Activate user account
export const activateUser = async (activationToken) => {
  try {
    const res = await apiClient.post('/user/activation', { activation_token: activationToken });
    return res.data;
  } catch (err) {
    console.error("Activation error:", err);
    throw err.response?.data?.message || err.response?.data?.msg || "Account activation failed";
  }
};

// Get user info
// export const getUserInfo = async () => {
//   try {
//     const res = await apiClient.get('/user/getuser');
//     return res.data;
//   } catch (err) {
//     console.error("Error fetching user info:", err);
//     throw err.response?.data?.message || err.response?.data?.msg || "Error fetching user information";
//   }
// };

export const getUserInfo = async () => {
  try {
    const res = await apiClient.get('/user/getuser', {
      withCredentials: true, 
    });
    console.log(res.data)
    return res.data;
  } catch (err) {
    console.error("Error fetching user info:", err);
    throw err.response?.data?.message || err.response?.data?.msg || "Error fetching user information";
  }
};

// Update user info
export const updateUserInfo = async (userData) => {
  try {
    const res = await apiClient.put('/user/update-user-info', userData);
    return res.data;
  } catch (err) {
    console.error("Error updating user info:", err);
    throw err.response?.data?.message || err.response?.data?.msg || "Error updating user information";
  }
};

// Logout user
export const logoutUser = async () => {
  try {
    const res = await apiClient.get('/user/logout');
    return res.data;
  } catch (err) {
    console.error("Logout error:", err);
    throw err.response?.data?.message || err.response?.data?.msg || "Logout failed";
  }
};

// Shop APIs
export const createShop = async (shopData) => {
  try {
    const formData = new FormData();
    
    // Add all shop fields
    Object.keys(shopData).forEach(key => {
      if (key !== 'avatar') {
        formData.append(key, shopData[key]);
      }
    });
    
    // Add avatar if exists
    if (shopData.avatar) {
      formData.append('avatar', shopData.avatar);
    }
    
    const res = await axios.post(`${BASE_URL}/shop/create-shop`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return res.data;
  } catch (err) {
    console.error("Shop creation error:", err);
    throw err.response?.data?.message || err.response?.data?.msg || "Shop creation failed";
  }
};

export const loginShop = async (loginData) => {
  try {
    const res = await apiClient.post('/shop/login-shop', loginData);
    return res.data;
  } catch (err) {
    console.error("Shop login error:", err);
    throw err.response?.data?.message || err.response?.data?.msg || "Shop login failed";
  }
};

export const activateShop = async (activationToken) => {
  try {
    const res = await apiClient.post('/shop/activation', { activation_token: activationToken });
    return res.data;
  } catch (err) {
    console.error("Shop activation error:", err);
    throw err.response?.data?.message || err.response?.data?.msg || "Shop activation failed";
  }
};

export const getShopInfo = async () => {
  try {
    const res = await apiClient.get('/shop/getSeller');
    return res.data;
  } catch (err) {
    console.error("Error fetching shop info:", err);
    throw err.response?.data?.message || err.response?.data?.msg || "Error fetching shop information";
  }
};

export const logoutShop = async () => {
  try {
    const res = await apiClient.get('/shop/logout');
    return res.data;
  } catch (err) {
    console.error("Shop logout error:", err);
    throw err.response?.data?.message || err.response?.data?.msg || "Shop logout failed";
  }
};

export const getShopById = async (shopId) => {
  try {
    const res = await apiClient.get(`/shop/get-shop-info/${shopId}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching shop by ID:", err);
    throw err.response?.data?.message || err.response?.data?.msg || "Error fetching shop information";
  }
};

// Chatbot API
export const sendChatbotMessage = async (message) => {
  try {
    const res = await apiClient.post('/chatbot/chat', { message });
    return res.data;
  } catch (err) {
    console.error("Chatbot error:", err);
    throw err.response?.data?.message || err.response?.data?.msg || "Chatbot error";
  }
};